/**
 * Traduction des textes que le JavaScript écrit à l'écran.
 *
 * Le script n'a pas accès à `_()`. Les chaînes vivent donc dans des blocs JSON
 * rendus par les gabarits, là où la traduction fonctionne, et sont relues ici.
 * Le site est servi en français ET en luxembourgeois : sans ce détour, un
 * visiteur de /lu/ lisait « Requis », « reste 60 sur 150 » ou « Réservé du … »
 * en français au milieu de sa page.
 *
 * Trois blocs alimentent la table :
 *   - `#materialStockLabels`      disponibilité du matériel  (form-rooms.njk)
 *   - `#bookingFormLabels`        récapitulatif d'erreurs, validation, dates
 *   - `#availabilityNoticeLabels` bandeau « disponibilités non chargées »
 *
 * Module partagé, et non copie par fichier : `_booking.js` et
 * `flatpickr-init-app.js` écrivent tous deux à l'usager, et leurs messages
 * doivent parler la même langue.
 *
 * Les valeurs par défaut ne sont pas de la redondance : elles couvrent un
 * gabarit plus ancien que le bundle, où le bloc JSON n'existe pas encore.
 */

const LIBELLES_PAR_DEFAUT = {
  remaining: 'reste {n} sur {total}',
  full: 'complet sur ces dates ({total})',
  shortage:
    "il ne reste que {n} exemplaire(s) sur la période demandée (vous en demandez {asked}).",
  none: 'plus aucun exemplaire disponible sur la période demandée.',
  fieldShortage:
    "Le matériel n'est pas disponible dans les quantités demandées. Quantité restante : {n}",
  changedTitle: 'La disponibilité du matériel a changé pendant votre saisie.',
  changedIntro:
    'Une autre réservation a été enregistrée entre-temps. Ajustez les quantités ci-dessous, ou modifiez vos dates — le reste de votre demande est conservé.',
  changedItem:
    "{name} : vous en demandez {asked}, il n'en reste que {n} sur la période choisie.",
  changedItemNone:
    '{name} : plus aucun exemplaire disponible sur la période choisie.',

  // Récapitulatif d'erreurs et validation de saisie. Rendus traduits par
  // `#bookingFormLabels` (form-rooms.njk) ; ces valeurs ne servent qu'au cas où
  // le gabarit serait plus ancien que le bundle.
  sommaireUn: '1 erreur à corriger',
  sommairePlusieurs: '{n} erreurs à corriger',
  sommaireIntro:
    "Votre demande n'a pas été envoyée. Corrigez les points suivants, puis renvoyez-la :",
  etape: 'Étape {n}',
  etape1: 'Contact',
  etape2: 'Configuration',
  etape3: 'Matériel',
  etape4: 'Confirmation',
  etapeAutres: 'Autres',
  requis: 'Ce champ est obligatoire',
  requisCase: 'Cochez cette case pour continuer',
  requisChoix: 'Choisissez une option',
  requisListe: 'Sélectionnez une valeur',
  emailInvalide: 'Adresse e-mail invalide — exemple : nom@exemple.lu',
  nombreMin: 'Le minimum est {min}',
  nombreMax: 'Le maximum est {max}',
  captchaLabel: 'Vérification anti-robot',
  captchaRequis: "Cochez « Je ne suis pas un robot » pour envoyer votre demande.",
  captchaExpire:
    'La vérification anti-robot a expiré. Cochez-la de nouveau, puis renvoyez votre demande.',
  envoiEnCours: 'Envoi en cours…',
  reseauTitre: "L'envoi n'a pas abouti.",
  reseauTexte:
    "Vérifiez votre connexion et réessayez. Rien n'est perdu : vos réponses sont toujours là.",
  conflitDates: 'Ces dates ne sont plus disponibles',
  serveurRequis: "Le serveur n'a pas reçu cette information",
  serveurRecaptcha: "La vérification anti-robot n'a pas abouti",

  // Cohérence des dates et des heures (flatpickr-init-app.js). Écrits en dur en
  // français jusqu'ici, sur un site servi aussi en luxembourgeois.
  conflitIntro: 'Votre demande entre en conflit avec des réservations existantes :',
  conflitPlage: 'Réservé du {debut} à {heureDebut} au {fin} à {heureFin}',
  conflitJour: 'Réservé toute la journée du {date}',
  conflitBouton:
    'Ces dates ne sont pas disponibles. Choisissez une autre période pour continuer.',
  finAvantDebut: "L'heure de fin doit être postérieure à l'heure de début.",
  finAvantDebutDate: 'La date de fin doit être postérieure à la date de début.',
  montageTropTard:
    "La période de montage/démontage doit couvrir toute la manifestation : elle commence après le début de l'événement.",
  montageTropTot:
    'La période de montage/démontage doit couvrir toute la manifestation : elle se termine avant la fin de l\'événement.',

  // Info-bulles des jours du calendrier et libellé de champ posé par le script.
  jourTropTot: 'Réservation impossible avant J+{n}',
  jourPartiel: 'Réservé : ',
  jourComplet: 'Jour complet — déjà réservé',
  placeholderEvenement: "Sélectionnez la période de l'événement",
};

let libellesStock = null;

/**
 * Traduit une clé et substitue ses paramètres.
 *
 * Deux blocs JSON alimentent la table : `#materialStockLabels` pour la
 * disponibilité du matériel, `#bookingFormLabels` pour le récapitulatif d'erreurs
 * et la validation de saisie. Séparés parce qu'ils sont rendus à deux endroits
 * différents du gabarit, fusionnés ici parce que le script n'a qu'un traducteur.
 */
export function t(cle, params = {}) {
  if (!libellesStock) {
    const fournis = {};
    ['materialStockLabels', 'bookingFormLabels', 'availabilityNoticeLabels'].forEach((id) => {
      const bloc = document.getElementById(id);
      if (!bloc) return;
      try {
        Object.assign(fournis, JSON.parse(bloc.textContent) || {});
      } catch (e) {
        /* bloc illisible : on garde les valeurs par défaut */
      }
    });
    libellesStock = { ...LIBELLES_PAR_DEFAUT, ...fournis };
  }

  const modele = libellesStock[cle] || LIBELLES_PAR_DEFAUT[cle] || '';
  return modele.replace(/\{(\w+)\}/g, (_m, nom) =>
    params[nom] === undefined ? `{${nom}}` : String(params[nom])
  );
}

